import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { Wand2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { AnalyticsService } from "@/services/analytics";
import {
  GenerateButton,
  PromptInput,
  UpgradePlansModal,
  AuthModal,
  MobileBottomBar,
} from "@/components/ui";
import { AssetSelectZone } from "@/components/features/image-to-video";
import { AssetPickerModal } from "@/components/features/shared";
import { PiecanNotice } from "@/components/features/piecan-notice";
import { POSITION_PRESETS, type PositionPreset } from "./position-presets";
import { useActiveGenerations } from "@/hooks";
import { useUserStore, hasAccessToPlanLevel } from "@/stores/user-store";
import type { Asset } from "@/types";

const IMAGE_EDIT_CREDIT_COST = 5;

interface ImageEditLocationState {
  /** Pre-fill the image preview from a generated/uploaded asset URL. */
  imageUrl?: string;
  /** Generation ID of a prior result, used as `input_url` for the edit. */
  imageId?: string;
  /** Upload ID if the source was an upload (forwarded as `upload_id`). */
  uploadId?: string;
  /**
   * Character key to carry forward — when set, the resulting edit is
   * bound to the same character so it doesn't appear as a loose asset.
   */
  folder?: string;
  /**
   * The image is an unsaved character draft — propagate the flag so
   * the next generation's success page also shows the Save / Edit /
   * Try Again CTAs. Cleared once the user explicitly Saves or
   * navigates away from the character flow.
   */
  isCharacterDraft?: boolean;
}

export function ImageEditForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn } = useUser();
  const { credits, plan } = useUserStore();
  const { hasActiveGeneration } = useActiveGenerations();
  const [prompt, setPrompt] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [inputGenerationId, setInputGenerationId] = useState<string | null>(null);
  const [folder, setFolder] = useState<string | null>(null);
  const [isCharacterDraft, setIsCharacterDraft] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<PositionPreset | null>(
    null,
  );

  // Pre-fill the image when arriving with nav state (from T2I result, asset
  // card "Edit", etc.). Tracks the consumed state in a ref so revisits with
  // a fresh state object trigger a re-fill, but back/forward without new
  // state preserves the user's in-progress edit.
  const consumedStateRef = useRef<unknown>(null);
  useEffect(() => {
    const navState = location.state as ImageEditLocationState | null;
    if (!navState?.imageUrl) return;
    if (consumedStateRef.current === navState) return;
    consumedStateRef.current = navState;

    setImagePreview(navState.imageUrl);
    setUploadId(navState.uploadId ?? null);
    setInputGenerationId(navState.imageId ?? null);
    setFolder(navState.folder ?? null);
    setIsCharacterDraft(!!navState.isCharacterDraft);
    // Clear the history state so a refresh doesn't re-prefill (and a back
    // nav doesn't reapply stale data).
    window.history.replaceState({}, document.title);
  }, [location.state]);
  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    preset: "no-credits" | "concurrent-limit";
    creditsNeeded?: number;
  }>({ open: false, preset: "no-credits" });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);

  // Check if user has Pro or Ultimate (unlimited concurrent generations)
  const hasUnlimitedConcurrent = hasAccessToPlanLevel(plan, "pro");

  const handleRemoveImage = () => {
    setImagePreview(null);
    setUploadId(null);
    setInputGenerationId(null);
  };

  const handleAssetSelect = (asset: Asset) => {
    // Set preview from asset URL
    setImagePreview(asset.url);

    // For uploaded assets, use upload_id; for generated assets, prefer
    // input_generation_id so the backend can fetch a fresh signed URL
    // instead of trusting whatever URL we have in hand.
    if (asset.source === "upload") {
      setUploadId(asset.id);
      setInputGenerationId(null);
    } else {
      setUploadId(null);
      setInputGenerationId(asset.id);
    }

    // If the picked asset belongs to a character (Characters tab in
    // the picker, or any character-bound generation) carry the
    // folder through so the resulting edit stays in that character.
    setFolder(asset.folder ?? null);

    setShowAssetPicker(false);
  };

  const handleGenerate = () => {
    if (!prompt.trim() || (!uploadId && !imagePreview)) return;

    if (!isSignedIn) {
      setShowAuthModal(true);
      return;
    }

    if (credits < IMAGE_EDIT_CREDIT_COST) {
      AnalyticsService.trackOutOfCredits({
        credits_remaining: credits,
        credits_required: IMAGE_EDIT_CREDIT_COST,
        model_attempted: 'qwen_image_edit',
        generation_type: 'image',
        is_paid_user: plan !== 'free',
        user_plan: plan,
      });
      setUpgradeModal({
        open: true,
        preset: "no-credits",
        creditsNeeded: IMAGE_EDIT_CREDIT_COST,
      });
      return;
    }

    // Check concurrent generation limit for non-Pro users
    if (hasActiveGeneration && !hasUnlimitedConcurrent) {
      setUpgradeModal({ open: true, preset: "concurrent-limit" });
      return;
    }

    // Append the readable position name to the edit instruction so
    // qwen-image-edit understands the act in plain English. The
    // leetspeak trigger (`d0gg13`, `m15510n4ry`, etc.) is sent
    // separately as `position_trigger_word` and the backend prepends
    // it at the very front of the prompt — that's what activates
    // Qwen4Play's per-act trained checkpoints.
    let finalPrompt = prompt.trim();
    if (selectedPosition?.readableName) {
      finalPrompt = `${finalPrompt}, ${selectedPosition.readableName}`;
    }

    // Navigate immediately to generation page with params. Prefer
    // `input_generation_id` (server-side resolved URL) over a raw
    // `input_url` whenever we have it — that's the cleanest path for
    // edits chained from a prior generation.
    navigate("/generation/new", {
      state: {
        modelId: "qwen_image_edit",
        prompt: finalPrompt,
        type: "image",
        generateParams: {
          model_id: "qwen_image_edit",
          prompt: finalPrompt,
          upload_id: uploadId || undefined,
          input_generation_id: inputGenerationId || undefined,
          input_url:
            !uploadId && !inputGenerationId && imagePreview
              ? imagePreview
              : undefined,
          folder: folder || undefined,
          // Qwen4Play's per-act trained checkpoint trigger. Backend
          // prepends this at position 0 of the final prompt before
          // submitting to the LoRA endpoint.
          position_trigger_word: selectedPosition?.triggerWord || undefined,
          // Backend persists this in `params.is_character_draft` on
          // the new generation, so the success page sees it even
          // after a refresh.
          is_character_draft: isCharacterDraft || undefined,
        },
        // Nav-state mirror — used by the success page during the
        // brief window before the new row's first status poll lands.
        isCharacterDraft: isCharacterDraft || undefined,
      },
    });
  };

  const isValid = (uploadId || imagePreview) && prompt.trim().length > 0;

  return (
    <>
      <div className="space-y-4 pb-24 md:pb-0">
        {/* Partner upsell — image-edit only accepts existing library assets,
            so upload-capable users get pointed at PieCan for the actual upload
            flow. Compact so the picker stays the primary focus. */}
        <PiecanNotice variant="compact" source="image_edit_form" />

        <AssetSelectZone
          onSelectFromAssets={() => setShowAssetPicker(true)}
          onRemove={handleRemoveImage}
          previewUrl={imagePreview}
        />

        <AssetPickerModal
          isOpen={showAssetPicker}
          onClose={() => setShowAssetPicker(false)}
          onSelect={handleAssetSelect}
          assetType="image"
          showFilters
        />

        <PromptInput
          value={prompt}
          onChange={setPrompt}
          placeholder="Describe how you want to edit the image... Change the background to a sunset, add a rainbow in the sky..."
          rows={2}
        />

        {/* Position presets — appended to the edit instruction as a
            natural-language hint (e.g. ", doggy style position"). The
            backend `applyI2iLoraTriggers` detects explicit acts in the
            final prompt and drops the prski skin LoRA when appropriate. */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-[#888]">Position</label>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {POSITION_PRESETS.map((position) => {
              const isSelected = selectedPosition?.id === position.id;
              return (
                <button
                  key={position.id}
                  type="button"
                  onClick={() =>
                    setSelectedPosition(isSelected ? null : position)
                  }
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-xl text-xs font-medium transition-all border",
                    isSelected
                      ? "bg-white/[0.1] border-white/[0.2] text-white"
                      : "bg-white/[0.03] border-white/[0.06] text-[#888] hover:bg-white/[0.06] hover:text-white",
                  )}
                >
                  <div
                    className={cn(
                      "w-full aspect-square rounded-lg overflow-hidden flex items-center justify-center bg-white/[0.05]",
                    )}
                  >
                    {position.thumbnail && (
                      <img
                        src={position.thumbnail}
                        alt={position.name}
                        className={cn(
                          "w-3/4 h-3/4 object-contain transition-opacity",
                          isSelected ? "opacity-100" : "opacity-40",
                        )}
                        loading="lazy"
                      />
                    )}
                  </div>
                  {position.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Generate Button - Desktop only */}
        <div className="hidden md:flex justify-end">
          <GenerateButton
            disabled={!isValid}
            className="w-full md:w-auto md:min-w-[200px]"
            onClick={handleGenerate}
          >
            <Wand2 className="size-5" />
            Edit Image ({IMAGE_EDIT_CREDIT_COST} credits)
          </GenerateButton>
        </div>
      </div>

      {/* Fixed bottom bar - Mobile only */}
      <MobileBottomBar>
        <GenerateButton
          disabled={!isValid}
          className="w-full"
          onClick={handleGenerate}
        >
          <Wand2 className="size-5" />
          Edit Image ({IMAGE_EDIT_CREDIT_COST} credits)
        </GenerateButton>
      </MobileBottomBar>

      <UpgradePlansModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, preset: "no-credits" })}
        preset={upgradeModal.preset}
        presetContext={{
          creditsNeeded: upgradeModal.creditsNeeded,
          currentCredits: credits,
        }}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
}
